import React from 'react';
import {
	Checkbox,
	InputField,
	Paragraph,
	Select,
	tokens,
} from '@welbe/web-design-system';
import { Button } from '@welbe/design-system';
import {
	Container,
	ExamContainer,
	ExamTitle,
	OtherSection,
	SelectContainer,
} from './RecommendedExams.styles';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { FiTrash } from 'react-icons/fi';
import { ExamSelected } from './RecommendedExams.types';
import { examTypes, frequencyTypes } from './RecommendedExams.const';
import { useRecommendedExams } from './RecommendedExams.hooks';
import { ExamProps } from 'Views/JobProfile/JobProfile.types';
import { StepProps } from '../../FormRisk.types';

export const RecommendedExams = ({
	recommended = [],
	steps,
}: {
	recommended: ExamProps[];
	steps: StepProps[];
}) => {
	const {
		isLoading,
		recommendedIds,
		jobExamProfileRiskFactors,
		jobRiskFactorsError,
		currentPosition,
		frequencyTypesOptions,
		isChecked,
		handleAddExam,
		getExamTypeValue,
		handleChangeDays,
		handleDeleteExam,
		getFrequencyValue,
		getDaysInitialValue,
		handleSelectExamType,
		handleSelectFrequency,
		handleCheckRecommendedExam,
	} = useRecommendedExams({ steps, recommended });

	if (isLoading) null;

	return (
		<Container>
			<Paragraph fontSize="18px" fontWeight="400" color={tokens.color_primary}>
				Estudios médicos recomendados
			</Paragraph>
			{recommended.map((exam) => (
				<ExamContainer key={exam.id}>
					<Checkbox
						id={exam.name}
						label={exam.name}
						data-testid={exam.name}
						topDistance="3px"
						value={isChecked(exam.id)}
						onChange={(state) => handleCheckRecommendedExam(state, exam)}
					/>
					{isChecked(exam.id) && (
						<SelectContainer
							height={
								getFrequencyValue(exam.id)?.id === 'other' ? '90px' : undefined
							}
						>
							<Select
								multiple
								topDistanceCheckBox="3px"
								id={`select-type-${exam.id}`}
								data-testid={`select-type-${exam.id}`}
								label="Tipo de examen"
								options={examTypes}
								initialSelected={getExamTypeValue(exam.id)}
								onChange={(selected) => handleSelectExamType(exam.id, selected)}
								invalidInput={
									!!jobRiskFactorsError[
										currentPosition
									].jobExamProfileRiskFactors.find(
										(examItem) => exam.id === examItem.examId,
									)?.type
								}
							/>
							<div>
								<Select
									id={`select-frequency-${exam.id}`}
									data-testid={`select-frequency-${exam.id}`}
									label="Frecuencia del examen"
									options={
										frequencyTypesOptions?.filter(
											(examFrequency) => examFrequency.id === exam.id,
										)[0]?.frequencyTypes || frequencyTypes
									}
									initialSelected={
										getFrequencyValue(exam.id) || { id: '', value: '' }
									}
									onChange={(selected) =>
										handleSelectFrequency(exam.id, selected)
									}
									invalidInput={
										!!jobRiskFactorsError[
											currentPosition
										].jobExamProfileRiskFactors.find(
											(examItem) => exam.id === examItem.examId,
										)?.frequency
									}
								/>
								{getFrequencyValue(exam.id)?.id === 'other' && (
									<OtherSection>
										<Paragraph fontWeight="500" margin="0 0 4px 0">
											Cada
										</Paragraph>
										<InputField
											className="input-frequency-other"
											type="number"
											min={0}
											width="70px"
											value={getDaysInitialValue(exam.id)}
											onChange={(days) => handleChangeDays(exam.id, days)}
										/>
										<Paragraph fontWeight="500" margin="0 0 4px 0">
											Días
										</Paragraph>
									</OtherSection>
								)}
							</div>
						</SelectContainer>
					)}
				</ExamContainer>
			))}
			<Paragraph fontWeight="500" color={tokens.color_primary}>
				¿Deseas agregar otros estudios?
			</Paragraph>
			<Autocomplete onAddExam={handleAddExam} />

			{jobExamProfileRiskFactors
				?.filter((item) => !recommendedIds.includes(item.exam.id))
				?.map((examItem: ExamSelected) => (
					<ExamContainer key={examItem.exam.id}>
						<ExamTitle>
							<Button
								width="40px"
								variant="ghost"
								onClick={() => handleDeleteExam(examItem.exam.id)}
							>
								<FiTrash />
							</Button>
							<Paragraph fontWeight="600">{examItem.exam.name}</Paragraph>
						</ExamTitle>
						<SelectContainer
							height={
								getFrequencyValue(examItem.exam.id)?.id === 'other'
									? '90px'
									: undefined
							}
						>
							<Select
								multiple
								topDistanceCheckBox="3px"
								id={`select-type-${examItem.exam.id}`}
								data-testid={`select-type-${examItem.exam.id}`}
								label="Tipo de examen"
								options={examTypes}
								initialSelected={getExamTypeValue(examItem.exam.id)}
								onChange={(selected) =>
									handleSelectExamType(examItem.exam.id, selected)
								}
								invalidInput={
									!!jobRiskFactorsError[
										currentPosition
									].jobExamProfileRiskFactors.find(
										(exam) => exam.examId === examItem.exam.id,
									)?.type
								}
							/>
							<div>
								<Select
									id={`select-frequency-${examItem.exam.id}`}
									data-testid={`select-frequency-${examItem.exam.id}`}
									label="Frecuencia del examen"
									options={
										frequencyTypesOptions?.filter(
											(examFrequency) => examFrequency.id === examItem.exam.id,
										)[0]?.frequencyTypes || frequencyTypes
									}
									initialSelected={
										getFrequencyValue(examItem.exam.id) || { id: '', value: '' }
									}
									onChange={(selected) =>
										handleSelectFrequency(examItem.exam.id, selected)
									}
									invalidInput={
										!!jobRiskFactorsError[
											currentPosition
										].jobExamProfileRiskFactors.find(
											(exam) => exam.examId === examItem.exam.id,
										)?.frequency
									}
								/>
								{getFrequencyValue(examItem.exam.id)?.id === 'other' && (
									<OtherSection>
										<Paragraph fontWeight="500" margin="0 0 4px 0">
											Cada
										</Paragraph>
										<InputField
											className="input-frequency-other"
											type="number"
											min={0}
											width="70px"
											value={getDaysInitialValue(examItem.exam.id)}
											onChange={(days) =>
												handleChangeDays(examItem.exam.id, days)
											}
										/>
										<Paragraph fontWeight="500" margin="0 0 4px 0">
											Días
										</Paragraph>
									</OtherSection>
								)}
							</div>
						</SelectContainer>
					</ExamContainer>
				))}
		</Container>
	);
};
